import {css, StyleSheet} from 'aphrodite';
import React, {useState, useMemo} from 'react';

import {pluralize} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PolymerSubtype, POLYMER_SUBTYPE_TO_MODEL_TYPE} from 'constants/MolbioConstants';
import * as NavConstants from 'constants/NavConstants';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getStore} from 'redux-stores/Store';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import {SearchUISelector} from 'search/reduceSearchUI';
import {Filter, FilterKey, keyToEmptyFilter, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {getAppliedFiltersFromUserPreference} from 'search/SearchPreferences';
import {updateParamsAndSearch} from 'search/SearchUIReduxActions';
import {clearSearchParams} from 'search/SearchUtil';
import withSearching, {SearchProps} from 'search/withSearching';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

type PolymerSearchViewNavSection =
  | NavConstants.Section.MOLBIO_BULK_ASSEMBLY
  | NavConstants.Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE
  | NavConstants.Section.MOD_PATTERNS;

const SECTION_TO_ACTION_SOURCE: {[Section in PolymerSearchViewNavSection]: ActionOrigin} = {
  [NavConstants.Section.MOLBIO_BULK_ASSEMBLY]: ActionOrigin.MOLBIO_BULK_ASSEMBLY,
  [NavConstants.Section.MOD_PATTERNS]: ActionOrigin.MODBIO_MOD_PATTERNS,
  [NavConstants.Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE]:
    ActionOrigin.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE,
};

export interface PolymerSearchViewProps extends PolymerSearchViewListingProps {
  limitSelectedEntitiesParams?: {
    maxNumPolymersToAdd: number;
    selectedEntityName: string;
  };
  initialFilters?: Array<Filter>;
}
interface PolymerSearchViewListingProps {
  navSection: PolymerSearchViewNavSection;
  polymerSubtypes: Array<PolymerSubtype>;
  rowSelectionMode: RowSelectionMode;
  selectedPolymerIds: Array<string>;
  setSelectedPolymerIds: (selectedIds: Array<string>) => void;
  alwaysShowSelector?: boolean;
  searchLimit?: number;
}

function PolymerSearchViewListing(props: PolymerSearchViewListingProps & SearchProps): JSX.Element {
  const [isCompactDisplay, setIsCompactDisplay] = useState<boolean>(true);

  return (
    <ItemListing
      {...props}
      isCompactDisplay={isCompactDisplay}
      setIsCompactDisplay={setIsCompactDisplay}
      isExpanded={true}
      activeItemId={null}
      enableToggleExpanded={false}
      // Override the regular withSearching state management and control the selectedIds externally.
      selectedIds={props.selectedPolymerIds}
      onSelectIds={props.setSelectedPolymerIds}
      actionSource={SECTION_TO_ACTION_SOURCE[props.navSection]}
      availableTypes={props.polymerSubtypes.map(
        (polymerSubtype) => POLYMER_SUBTYPE_TO_MODEL_TYPE[polymerSubtype]
      )}
      makeCrumbs={undefined}
      extraPrimaryFilterOptions={[{filterKey: FilterKey.ANCESTOR_FOLDER}]}
      availableSortKeys={[
        SortKey.SORT_NAME_KEY,
        SortKey.SORT_IDENTIFIER_KEY,
        SortKey.SORT_MODIFIED_AT_KEY,
        SortKey.SORT_CREATED_AT_KEY,
      ]}
      renderToolbar={() => <></>}
      preservedFilterKeys={[]}
      section={props.navSection}
      disableOpenItem={true}
      dataTestAttrs={{component: 'PolymerSearchView', element: 'root'}}
      disableBLAST={true}
      rowSelectionMode={props.rowSelectionMode}
      alwaysShowSelector={props.alwaysShowSelector}
    />
  );
}

function getDefaultSearchParams(
  polymerSubtypes: Array<PolymerSubtype>,
  navSection: PolymerSearchViewNavSection,
  extraFilters?: Array<Filter>,
  searchLimit?: number
): SearchParams {
  const filters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: polymerSubtypes.map((polymerSubtype) => POLYMER_SUBTYPE_TO_MODEL_TYPE[polymerSubtype]),
    },
    ...getAppliedFiltersFromUserPreference(navSection),
    ...(extraFilters ?? []),
  ];
  return {
    filters,
    offset: 0,
    limit: searchLimit ?? 25,
    query: '',
    sorts: [],
  };
}

/**
 * Helper to clear any cached search params associated with the search view.
 */
export function clearPolymerSearchViewSearchParams(
  polymerSubtypes: Array<PolymerSubtype>,
  navSection: PolymerSearchViewNavSection,
  initialFilters?: Array<Filter>
): void {
  const store = getStore();
  const searchUISelector = new SearchUISelector(store.getState());
  const defaultSearchParams = getDefaultSearchParams(polymerSubtypes, navSection);
  const existingSearchParams =
    searchUISelector.getSearchSessionState(navSection)?.searchParams ?? defaultSearchParams;

  const newParams = clearSearchParams(
    existingSearchParams,
    [FilterKey.ANCESTOR_FOLDER],
    [
      {
        ...keyToEmptyFilter(FilterKey.TYPES),
        value: polymerSubtypes.map((polymerSubtype) => POLYMER_SUBTYPE_TO_MODEL_TYPE[polymerSubtype]),
      },
      ...(initialFilters ?? []),
    ]
  );
  store.dispatch(
    updateParamsAndSearch(
      navSection,
      SearchType.FOLDERS_AND_ITEMS,
      newParams,
      getEmptyFilterErrors(newParams.filters)
    )
  );
}

/**
 * A search view for the given polymer types.
 *
 * The exact polymer model types supported are controlled by the `polymerSubtypes` prop,
 * so callers can limit results to just RNA Oligos for instance, or any combination of polymer subtypes.
 * Component is backed by ItemListing and provides functionality similar to global search including
 * schema field and type filters.
 */
export default function PolymerSearchView({
  polymerSubtypes,
  limitSelectedEntitiesParams,
  navSection,
  searchLimit,
  initialFilters,
  ...otherProps
}: PolymerSearchViewProps): JSX.Element {
  // Important to memoize because `withSearching` is an HOC that performs a query, so it will cause the React
  // tree to unmount everytime PolymerSearchView re-renders if we don't memoize this result.
  const SearchingComponent = useMemo(
    () =>
      withSearching(PolymerSearchViewListing, SearchType.FOLDERS_AND_ITEMS, navSection, {
        defaultSearchParams: getDefaultSearchParams(polymerSubtypes, navSection, initialFilters, searchLimit),
        filterKeysToRemember: [FilterKey.ANCESTOR_FOLDER],
      }),
    [polymerSubtypes, navSection, searchLimit, initialFilters]
  );

  const {selectedPolymerIds} = otherProps;

  return (
    <>
      {limitSelectedEntitiesParams != null &&
        selectedPolymerIds.length > limitSelectedEntitiesParams.maxNumPolymersToAdd && (
          <div className={css(styles.banner)}>
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text={`Adding all of the selected sequences would exceed the maximum allowed number of ${limitSelectedEntitiesParams.selectedEntityName}. Remove ${selectedPolymerIds.length - limitSelectedEntitiesParams.maxNumPolymersToAdd} ${pluralize(selectedPolymerIds.length - limitSelectedEntitiesParams.maxNumPolymersToAdd, 'sequence')} to continue.`}
            />
          </div>
        )}
      <SearchingComponent
        polymerSubtypes={polymerSubtypes}
        searchLimit={searchLimit}
        navSection={navSection}
        {...otherProps}
      />
    </>
  );
}

const styles = StyleSheet.create({
  banner: {
    padding: '15px',
  },
});
